/**
 * UserBudgetOverrideDialog
 * Dialog for setting or removing a user's budget override.
 */
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Loader2 } from 'lucide-react'
import {
  useSetUserBudgetOverride,
  useRemoveUserBudgetOverride,
} from '@/hooks/useBudgetAdmin'
import type { UserBudgetInfo } from '@/types/budgetAdmin'
import { formatBudgetUsd } from '@/types/budgetAdmin'

interface UserBudgetOverrideDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: UserBudgetInfo
}

export function UserBudgetOverrideDialog({
  open,
  onOpenChange,
  user,
}: UserBudgetOverrideDialogProps) {
  const [budgetValue, setBudgetValue] = useState(
    user.hasOverride && user.effectiveBudgetUsd !== null
      ? String(user.effectiveBudgetUsd)
      : ''
  )
  const [reason, setReason] = useState(user.overrideReason ?? '')
  const [error, setError] = useState<string | null>(null)

  const setOverride = useSetUserBudgetOverride()
  const removeOverride = useRemoveUserBudgetOverride()

  const handleSave = async () => {
    setError(null)

    const budgetNum = parseFloat(budgetValue)
    if (isNaN(budgetNum) || budgetNum < 0) {
      setError('Please enter a valid budget amount (0 or greater)')
      return
    }

    try {
      await setOverride.mutateAsync({
        oid: user.oid,
        request: {
          budgetUsd: budgetNum,
          reason: reason.trim() || undefined,
        },
      })
      onOpenChange(false)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to set budget override'
      )
    }
  }

  const handleRemove = async () => {
    setError(null)
    try {
      await removeOverride.mutateAsync(user.oid)
      onOpenChange(false)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to remove budget override'
      )
    }
  }

  const isPending = setOverride.isPending || removeOverride.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {user.hasOverride ? 'Edit Budget Override' : 'Set Budget Override'}
          </DialogTitle>
          <DialogDescription>
            {user.hasOverride
              ? `Modify the budget override for ${user.displayName}.`
              : `Set a custom budget limit for ${user.displayName}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Info */}
          <div className="text-sm text-muted-foreground">
            <p>
              <strong>Email:</strong> {user.email}
            </p>
            <p>
              <strong>Role:</strong> {user.roleCode}
            </p>
            <p>
              <strong>Current Limit:</strong>{' '}
              {formatBudgetUsd(user.effectiveBudgetUsd)} ({user.budgetSource})
            </p>
            <p>
              <strong>Current Usage:</strong> $
              {user.currentMonthUsageUsd.toFixed(2)}
            </p>
          </div>

          {/* Budget Input */}
          <div className="space-y-2">
            <Label htmlFor="budget">Monthly Budget (USD)</Label>
            <Input
              id="budget"
              type="number"
              min="0"
              step="1"
              placeholder="e.g., 100"
              value={budgetValue}
              onChange={(e) => setBudgetValue(e.target.value)}
              disabled={isPending}
            />
          </div>

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              placeholder="Why is this override needed?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isPending}
              rows={2}
            />
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:justify-between">
          <div>
            {user.hasOverride && (
              <Button
                variant="destructive"
                onClick={handleRemove}
                disabled={isPending}
              >
                {removeOverride.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Remove Override
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending || !budgetValue}>
              {setOverride.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Override
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
